import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * Cart drawer controller.
 *
 * THE APPLIANCE BARN
 *
 * Behaviour:
 * - Any successful add-to-cart opens the cart drawer.
 * - Product page adds open the drawer.
 * - Collection / quick-add opens the drawer.
 * - Appliance + delivery service additions open the drawer.
 * - Existing cart drawer rendering remains owned by Tinker.
 * - Does not interfere with cart quantity changes/removals.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {number | null} */
  #openTimer = null;


  /**
   * Tinker theme drawer containing this cart.
   *
   * @returns {import('@theme/theme-drawer').ThemeDrawer | null}
   */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
      this.closest('theme-drawer')
    );
  }


  /**
   * Native dialog.
   *
   * @returns {HTMLDialogElement | null}
   */
  get #dialog() {
    return this.closest('dialog');
  }


  connectedCallback() {
    super.connectedCallback();


    /*
     * Listen for Tinker's standard cart line updates.
     */
    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );


    /*
     * Also listen for Shopify's standard cart event directly.
     *
     * This gives us coverage for:
     * - product page
     * - collection page
     * - quick add
     * - custom delivery-service additions
     */
    document.addEventListener(
      'shopify:cart:lines-update',
      this.#handleStandardCartUpdate
    );


    this.#themeDrawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );


    /*
     * The restore path can set [open] before this
     * module has loaded.
     */
    if (
      this.#themeDrawer?.hasAttribute(
        'open'
      )
    ) {
      this.#handleDrawerOpen();
    }
  }


  disconnectedCallback() {
    super.disconnectedCallback();


    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );


    document.removeEventListener(
      'shopify:cart:lines-update',
      this.#handleStandardCartUpdate
    );


    this.#themeDrawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );


    if (
      this.#openTimer !== null
    ) {
      window.clearTimeout(
        this.#openTimer
      );

      this.#openTimer =
        null;
    }
  }


  /**
   * Handles drawer opening.
   *
   * Updates sticky state and keeps the
   * installments CTA behaviour from Tinker.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();


    customElements
      .whenDefined(
        'shopify-payment-terms'
      )
      .then(
        () => {
          const cta =
            this
              .querySelector(
                'shopify-payment-terms'
              )
              ?.shadowRoot
              ?.querySelector(
                '#shopify-installments-cta'
              );


          cta?.addEventListener(
            'click',
            () =>
              this.#themeDrawer?.close(),
            {
              once: true
            }
          );
        }
      );
  };


  /**
   * Tinker's normal cart line update handler.
   *
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (
    event
  ) => {
    const isAdd =
      event.action === 'add' ||
      event.detail?.action === 'add';


    /*
     * Quantity changes and removals should not
     * unexpectedly open the drawer.
     */
    if (!isAdd) {
      event.promise
        ?.then(
          () => {
            requestAnimationFrame(
              () =>
                this.#updateStickyState()
            );
          }
        )
        .catch(
          error => {
            if (
              error?.name !==
              'AbortError'
            ) {
              console.warn(
                '[cart-drawer] Event promise rejected:',
                error
              );
            }
          }
        );

      return;
    }


    /*
     * Quick-add can live inside a modal.
     *
     * Allow that modal to close and restore
     * focus before opening the cart drawer.
     */
    const sourceModal =
      /** @type {HTMLDialogElement | null} */ (
        event.target instanceof Element
          ? event.target.closest(
              'dialog:modal'
            )
          : null
      );


    const openAndSettle =
      () => {
        this.#scheduleOpen();

        requestAnimationFrame(
          () =>
            this.#updateStickyState()
        );
      };


    /*
     * If the event has a promise, wait for the
     * actual cart mutation/render to finish.
     *
     * This prevents the old:
     *
     * EMPTY DRAWER
     * -> close
     * -> correct drawer
     *
     * problem.
     */
    if (event.promise) {
      event.promise
        .then(
          ({ detail } = {}) => {
            if (
              detail?.didError
            ) {
              this.#updateStickyState();
              return;
            }


            if (
              sourceModal?.open
            ) {
              sourceModal.addEventListener(
                'close',
                openAndSettle,
                {
                  once: true
                }
              );
            } else {
              openAndSettle();
            }
          }
        )
        .catch(
          error => {
            if (
              error?.name !==
              'AbortError'
            ) {
              console.warn(
                '[cart-drawer] Event promise rejected:',
                error
              );
            }
          }
        );

      return;
    }


    /*
     * No promise supplied.
     *
     * Give Tinker a moment to update its
     * cart sections before opening.
     */
    if (
      sourceModal?.open
    ) {
      sourceModal.addEventListener(
        'close',
        openAndSettle,
        {
          once: true
        }
      );
    } else {
      this.#scheduleOpen();
    }
  };


  /**
   * Fallback listener for Shopify's standard
   * cart line update event.
   *
   * This catches add-to-cart flows which do
   * not reach the typed StandardEvents listener
   * in the way Tinker expects.
   *
   * @param {Event} event
   */
  #handleStandardCartUpdate = (
    event
  ) => {
    const action =
      event.action ||
      event.detail?.action;


    if (
      action !== 'add'
    ) {
      return;
    }


    /*
     * If the event carries a promise, wait for
     * the cart operation to settle first.
     */
    if (event.promise) {
      Promise.resolve(
        event.promise
      )
        .then(
          result => {
            const didError =
              result?.detail?.didError ||
              result?.didError;


            if (didError) {
              return;
            }


            this.#scheduleOpen();
          }
        )
        .catch(
          error => {
            if (
              error?.name !==
              'AbortError'
            ) {
              console.warn(
                '[cart-drawer] Cart update rejected:',
                error
              );
            }
          }
        );

      return;
    }


    this.#scheduleOpen();
  };


  /**
   * Open the existing Tinker cart drawer.
   *
   * Multiple events can be fired for the same
   * cart operation. Debouncing here prevents
   * two attempts to open the drawer.
   */
  #scheduleOpen() {
    if (
      this.#openTimer !== null
    ) {
      window.clearTimeout(
        this.#openTimer
      );
    }


    this.#openTimer =
      window.setTimeout(
        () => {
          this.#openTimer =
            null;


          if (
            !this.#themeDrawer
          ) {
            return;
          }


          /*
           * Already open: just refresh the
           * sticky calculations.
           */
          if (
            this.#themeDrawer.isOpen
          ) {
            this.#updateStickyState();
            return;
          }


          /*
           * Don't open an empty cart drawer.
           *
           * By this point Tinker should already
           * have rendered the updated cart.
           */
          if (
            this.#isCartEmpty()
          ) {
            /*
             * Cart markup can occasionally land
             * one frame after the event.
             *
             * Give it one final short wait.
             */
            window.setTimeout(
              () => {
                if (
                  !this.#isCartEmpty() &&
                  !this.#themeDrawer?.isOpen
                ) {
                  this.#themeDrawer?.open();
                }
              },
              120
            );

            return;
          }


          this.#themeDrawer.open();
        },
        80
      );
  }


  /**
   * Is the currently rendered drawer empty?
   */
  #isCartEmpty() {
    return Boolean(
      this.querySelector(
        '.cart-drawer--empty'
      )
    );
  }


  /**
   * Maintain Tinker's sticky cart summary.
   */
  #updateStickyState() {
    const dialog =
      this.#dialog;


    if (!dialog) {
      return;
    }


    /*
     * Refs don't cross nested component
     * boundaries, so query inside dialog.
     */
    const content =
      dialog.querySelector(
        '.cart-drawer__content'
      );


    const summary =
      dialog.querySelector(
        '.cart-drawer__summary'
      );


    if (
      !content ||
      !summary
    ) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      return;
    }


    const drawerHeight =
      dialog
        .getBoundingClientRect()
        .height;


    const summaryHeight =
      summary
        .getBoundingClientRect()
        .height;


    if (
      !drawerHeight
    ) {
      return;
    }


    const ratio =
      summaryHeight /
      drawerHeight;


    dialog.setAttribute(
      'cart-summary-sticky',
      ratio >
        this.#summaryThreshold
        ? 'false'
        : 'true'
    );
  }
}


if (
  !customElements.get(
    'cart-drawer-component'
  )
) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}